// Data hooks for API-backed application surfaces. Signed-out users are
// handled by AuthScreen, so account and server data never enters the shell
// without an authenticated session.
//
// Convention:
//   - Each hook returns `{ status, data }` where `status` is one of
//     "idle" | "loading" | "ready" | "error".
//   - Mutations optimistically update local state, then reconcile with the
//     server response when the surface is ready.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiCallError, api, type WireMessage } from "../lib/api";
import { knownBadges } from "../lib/badges";
import { reactionsForViewer, setOwnReaction } from "../lib/message-reactions";
import { readSessionToken } from "../lib/http";
import { beginUpdateActivity } from "../lib/update-activity";
import { resolveReplyPreviews } from "../lib/message-replies";
import { currentDmSoundEnabled, installDmAudio, isConversationVisible, parseDmNotice, playDmSound, shouldChime } from "../lib/dm-notifications";
import type {
  ChannelView,
  DmView,
  FriendView,
  MessageView,
  PendingFriendView,
  SpaceView,
} from "../types/ui";
import { useRealtime, useRealtimeSubscription } from "./Realtime";
import { useSelf } from "./SelfSession";

export type DataStatus = "idle" | "loading" | "ready" | "error";

const EMPTY_PERSONAL_SPACE: SpaceView = {
  xid: "",
  spaceType: "personal_workspace",
  name: "My Corner",
  iconLabel: "M",
  iconUrl: null,
};

interface DataResult<T> {
  status: DataStatus;
  data: T;
  error?: string;
}

/**
 * Read the current session token once per render. The token lives
 * in sessionStorage so this is cheap and synchronous.
 */
function authed(): boolean {
  const t = readSessionToken();
  return t !== null && t.length > 0;
}

function useRealtimeResync(refresh: () => void): void {
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.addEventListener("link:realtime-resync", refresh);
    return () => window.removeEventListener("link:realtime-resync", refresh);
  }, [refresh]);
}

// ---------------------------------------------------------------------------
// Spaces
// ---------------------------------------------------------------------------

export function useSpaces(): DataResult<{
  personal: SpaceView;
  spaces: SpaceView[];
}> & { refresh: () => void } {
  const self = useSelf();
  const [state, setState] = useState<DataResult<{
    personal: SpaceView;
    spaces: SpaceView[];
  }>>({
    status: "loading",
    data: { personal: EMPTY_PERSONAL_SPACE, spaces: [] },
  });
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!authed()) return;
    let cancelled = false;
    api.spaces
      .list()
      .then((res) => {
        if (cancelled) return;
        const personal =
          res.spaces
            .filter((s) => s.space_type === "personal_workspace")
            .map(toSpaceView)[0] ?? EMPTY_PERSONAL_SPACE;
        const others = res.spaces
          .filter((s) => s.space_type !== "personal_workspace")
          .map(toSpaceView);
        setState({
          status: "ready",
          data: { personal, spaces: others },
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: "error",
          data: { personal: EMPTY_PERSONAL_SPACE, spaces: [] },
          error: messageOf(err),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [version]);

  const refresh = useCallback(() => setVersion((value) => value + 1), []);
  const spaceTopics = useMemo(
    () =>
      [`account:${self.identity.accountXid}`, ...[state.data.personal, ...state.data.spaces]
        .map((space) => space.xid)
        .filter((xid) => xid.length > 0)
        .map((xid) => `space:${xid}`)],
    [self.identity.accountXid, state.data.personal, state.data.spaces],
  );
  useRealtimeSubscription(spaceTopics, (frame) => {
    if (
      frame.type === "space.updated"
      || frame.type === "space.member_added"
      || frame.type === "space.member_removed"
    ) refresh();
  });
  useRealtimeResync(refresh);
  return { ...state, refresh };
}

// ---------------------------------------------------------------------------
// Channels in a space
// ---------------------------------------------------------------------------

export function useChannels(spaceXid: string | null): DataResult<ChannelView[]> {
  const [state, setState] = useState<DataResult<ChannelView[]>>(() => ({
    status: authed() ? "loading" : "idle",
    data: [],
  }));
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((value) => value + 1), []);

  useEffect(() => {
    if (spaceXid === null) {
      setState({ status: "idle", data: [] });
      return;
    }
    if (!authed()) {
      setState({
        status: "idle",
        data: [],
      });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, status: "loading" }));
    api.spaces
      .channels(spaceXid)
      .then((res) => {
        if (cancelled) return;
        setState({
          status: "ready",
          data: res.channels.map(toChannelView),
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: "error",
          data: [],
          error: messageOf(err),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [spaceXid, version]);

  useRealtimeSubscription(spaceXid === null ? [] : [`space:${spaceXid}`], (frame) => {
    if (frame.type === "space.updated") refresh();
  });
  useRealtimeResync(refresh);

  return state;
}

// ---------------------------------------------------------------------------
// Friends + Pending
// ---------------------------------------------------------------------------

export function useFriends(): DataResult<{
  friends: FriendView[];
  pending: PendingFriendView[];
  blocked: FriendView[];
}> & { refresh: () => void } {
  const self = useSelf();
  const [state, setState] = useState<DataResult<{
    friends: FriendView[];
    pending: PendingFriendView[];
    blocked: FriendView[];
  }>>({
    status: authed() ? "loading" : "idle",
    data: { friends: [], pending: [], blocked: [] },
  });
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((value) => value + 1), []);

  useEffect(() => {
    if (!authed()) return;
    let cancelled = false;
    api.friends
      .list()
      .then((res) => {
        if (cancelled) return;
        const accepted = res.friends.filter((friend) => friend.status === "accepted");
        const pending = res.friends.filter((friend) => friend.status === "pending");
        const blocked = res.friends.filter((friend) => friend.status === "blocked");
        setState({
          status: "ready",
          data: {
            friends: accepted.map((f) => ({
              xid: f.account_xid,
              name: f.display_name ?? f.account_xid,
              username: f.username,
              avatarUrl: f.avatar_url,
              bannerColor: f.banner_color,
              bannerMode: f.banner_mode,
              bannerSecondaryColor: f.banner_secondary_color,
              pronouns: f.pronouns,
              tag: f.account_xid.slice(-4),
              status: f.presence,
              note: f.custom_status ?? null,
              memberSince: f.member_since,
              badges: knownBadges(f.badges),
              communityTag: f.community_tag,
            })),
            pending: pending.map((f) => ({
              xid: f.account_xid,
              name: f.display_name ?? f.account_xid,
              username: f.username,
              avatarUrl: f.avatar_url,
              bannerColor: f.banner_color,
              bannerMode: f.banner_mode,
              bannerSecondaryColor: f.banner_secondary_color,
              pronouns: f.pronouns,
              tag: f.account_xid.slice(-4),
              status: f.presence,
              note: f.custom_status ?? null,
              memberSince: f.member_since,
              badges: knownBadges(f.badges),
              communityTag: f.community_tag,
              direction: f.direction,
            })),
            blocked: blocked.map((f) => ({
              xid: f.account_xid,
              name: f.display_name ?? f.account_xid,
              username: f.username,
              avatarUrl: f.avatar_url,
              bannerColor: f.banner_color,
              bannerMode: f.banner_mode,
              bannerSecondaryColor: f.banner_secondary_color,
              pronouns: f.pronouns,
              tag: f.account_xid.slice(-4),
              status: f.presence,
              note: f.custom_status ?? null,
              memberSince: f.member_since,
              badges: knownBadges(f.badges),
              communityTag: f.community_tag,
            })),
          },
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: "error",
          data: { friends: [], pending: [], blocked: [] },
          error: messageOf(err),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [version]);

  const friendTopics = useMemo(() => {
    const accountXids = new Set<string>([self.identity.accountXid]);
    for (const friend of [...state.data.friends, ...state.data.pending]) {
      accountXids.add(friend.xid);
    }
    return Array.from(accountXids)
      .filter((xid) => xid.length > 0)
      .map((xid) => `account:${xid}`);
  }, [self.identity.accountXid, state.data.friends, state.data.pending]);
  useRealtimeSubscription(friendTopics, (frame) => {
    if (
      frame.type === "friend.request_received" ||
      frame.type === "friend.request_accepted" ||
      frame.type === "friend.updated" ||
      frame.type === "profile.updated" ||
      frame.type === "community_tag.updated"
    ) {
      refresh();
      return;
    }
    if (frame.type !== "presence.online" && frame.type !== "presence.offline") return;
    const payload = frame.payload as { account_xid?: unknown; presence?: unknown };
    if (typeof payload.account_xid !== "string") return;
    const status = payload.presence === "online"
      || payload.presence === "away"
      || payload.presence === "busy"
      || payload.presence === "offline"
      ? payload.presence
      : frame.type === "presence.online" ? "online" : "offline";
    const updatePresence = <T extends FriendView | PendingFriendView>(items: T[]): T[] =>
      items.map((friend) => friend.xid === payload.account_xid ? { ...friend, status } : friend);
    setState((current) => ({
      ...current,
      data: {
        friends: updatePresence(current.data.friends),
        pending: updatePresence(current.data.pending),
        blocked: updatePresence(current.data.blocked),
      },
    }));
  });
  useRealtimeResync(refresh);

  return { ...state, refresh };
}

// ---------------------------------------------------------------------------
// DMs
// ---------------------------------------------------------------------------

export function useDms(activeDmXid: string | null = null): DataResult<DmView[]> & { refresh: () => void } {
  const self = useSelf();
  const realtime = useRealtime();
  const [version, setVersion] = useState(0);
  const [state, setState] = useState<DataResult<DmView[]>>({
    status: authed() ? "loading" : "idle",
    data: [],
  });
  const refresh = useCallback(() => setVersion((value) => value + 1), []);
  // One authoritative refresh per burst; replay must never inflate unread counts.
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queueRefresh = useCallback(() => {
    if (refreshTimer.current !== null) return;
    refreshTimer.current = setTimeout(() => { refreshTimer.current = null; refresh(); }, 150);
  }, [refresh]);
  useEffect(() => () => {
    if (refreshTimer.current !== null) clearTimeout(refreshTimer.current);
    refreshTimer.current = null;
  }, []);
  useEffect(installDmAudio, []);

  const lastReadAttempt = useRef<string | null>(null);
  useEffect(() => {
    const readVisible = () => {
      const dm = state.data.find(item => item.xid === activeDmXid);
      if (!dm || !dm.latestMessageXid || dm.unreadCount === 0
        || !isConversationVisible(activeDmXid, dm.xid, document.visibilityState === "visible", document.hasFocus())) return;
      const key = `${dm.xid}:${dm.latestMessageXid}`;
      if (lastReadAttempt.current === key) return;
      lastReadAttempt.current = key;
      void api.dms.setLastRead(dm.xid, { last_read_message_xid: dm.latestMessageXid })
        .then(queueRefresh).catch(() => { if (lastReadAttempt.current === key) lastReadAttempt.current = null; });
    };
    readVisible();
    window.addEventListener("focus", readVisible);
    document.addEventListener("visibilitychange", readVisible);
    return () => { window.removeEventListener("focus", readVisible); document.removeEventListener("visibilitychange", readVisible); };
  }, [activeDmXid, state.data, queueRefresh]);

  useEffect(() => {
    if (!authed()) return;
    let cancelled = false;
    api.dms
      .list()
      .then((res) => {
        if (cancelled) return;
        setState({
          status: "ready",
          data: res.dms.map((d) => {
            const others = d.participants.filter(
              (participant) => participant.account_xid !== self.identity.accountXid,
            );
            // UI participants exclude self; encryption recipients below still
            // include everyone. A solo group must not count its creator twice.
            const visible = others;
            return {
            xid: d.xid,
            isGroup: d.is_group,
            ownerAccountXid: d.owner_account_id,
            pinned: d.pinned,
            muted: d.muted,
            name: d.is_group
              ? d.name ?? (visible.length === 0 ? "New group" : visible.map((participant) => participant.display_name ?? participant.account_xid).join(", "))
              : visible[0]?.display_name ?? visible[0]?.account_xid ?? "Direct message",
            participants: visible.map((p) => ({
              xid: p.account_xid,
              name: p.display_name ?? p.account_xid,
              username: p.username,
              avatarUrl: p.avatar_url,
              bannerColor: p.banner_color,
              bannerMode: p.banner_mode,
              bannerSecondaryColor: p.banner_secondary_color,
              pronouns: p.pronouns,
              memberSince: p.member_since,
              badges: knownBadges(p.badges),
              communityTag: p.community_tag,
              tag: p.account_xid.slice(-4),
              status: p.presence,
              note: null,
            })),
            lastActivityAt: d.latest_message_at ?? d.created_at,
            latestMessageXid: d.latest_message_xid,
            unreadCount: d.unread_count,
            preview: "",
            recipients: d.participants.map((p) => ({
              accountIdHashB64: p.account_id_hash_b64,
              publicKeyB64: p.public_key_b64,
            })),
          };
          }).sort((left, right) => Number(right.pinned) - Number(left.pinned)
            || Date.parse(right.lastActivityAt) - Date.parse(left.lastActivityAt)),
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: "error",
          data: [],
          error: messageOf(err),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [self.identity.accountXid, version]);

  const presenceTopics = useMemo(() => {
    const topics = new Set<string>();
    if (self.identity.accountXid.length > 0) topics.add(`account:${self.identity.accountXid}`);
    for (const dm of state.data) {
      for (const participant of dm.participants) topics.add(`account:${participant.xid}`);
    }
    return Array.from(topics);
  }, [self.identity.accountXid, state.data]);
  useRealtimeSubscription(presenceTopics, (frame) => {
    if (frame.type === "dm.message_created" && frame.topic === `account:${self.identity.accountXid}`) {
      const notice = parseDmNotice(frame.payload);
      queueRefresh();
      if (notice && shouldChime(notice, {
        account: self.identity.accountXid, active: activeDmXid,
        visible: document.visibilityState === "visible", focused: document.hasFocus(),
        enabled: currentDmSoundEnabled(), busy: self.status === "busy", deafened: self.deafened,
        replayed: frame.replayed === true, now: realtime.serverNow(),
      })) void playDmSound(self.identity.accountXid, notice.message_xid);
      return;
    }
    if (
      frame.type === "dm.updated"
      || frame.type === "dm.created"
      || frame.type === "dm.participant_added"
      || frame.type === "dm.participant_removed"
      || frame.type === "profile.updated"
      || frame.type === "community_tag.updated"
    ) {
      queueRefresh();
      return;
    }
    if (frame.type !== "presence.online" && frame.type !== "presence.offline") {
      return;
    }
    const payload = frame.payload as { account_xid?: unknown; presence?: unknown };
    const accountXid = payload.account_xid;
    if (typeof accountXid !== "string") return;
    const status = payload.presence === "online"
      || payload.presence === "away"
      || payload.presence === "busy"
      || payload.presence === "offline"
      ? payload.presence
      : frame.type === "presence.online" ? "online" : "offline";
    setState((current) => ({
      ...current,
      data: current.data.map((dm) => ({
        ...dm,
        participants: dm.participants.map((participant) =>
          participant.xid === accountXid
            ? { ...participant, status }
            : participant,
        ),
      })),
    }));
  });

  const dmTopics = useMemo(
    () => state.data.map((dm) => `dm:${dm.xid}`),
    [state.data],
  );
  useRealtimeSubscription(dmTopics, (frame) => {
    if (
      frame.type === "dm.updated"
      || frame.type === "dm.participant_added"
      || frame.type === "dm.participant_removed"
    ) {
      refresh();
      return;
    }
    if (frame.type === "message.created" || frame.type === "message.deleted") queueRefresh();
  });
  useRealtimeResync(refresh);

  return { ...state, refresh };
}

// ---------------------------------------------------------------------------
// Messages — the most complex hook. Loads on mount, listens to the
// realtime topic for new events, and exposes mutation helpers that
// optimistically update local state before the API call settles.
//
// E2E channels (Personal Workspace, Secrets, DMs) encrypt outgoing
// bodies via `@link/crypto` before the wire send, and decrypt
// incoming envelopes before rendering. The encryption boundary is
// kept inside this hook so call sites stay simple — they pass
// `channelKind` and the hook handles the rest.
// ---------------------------------------------------------------------------

import {
  buildE2eAad,
  decryptE2e,
  encryptE2e,
  parseE2eEnvelope,
  serializeE2eEnvelope,
  type E2eRecipient,
} from "@link/crypto";

import { loadIdentity } from "../lib/identity";

const TEXT_ENCODER = new TextEncoder();
const TEXT_DECODER = new TextDecoder();

/**
 * Channel kind hint passed to `useMessages`. Drives the send +
 * receive paths:
 *
 *   - `"atrest"` — server-side encryption under the per-Space PSK.
 *     Used by shared workspace + community text/snippets/files
 *     channels.
 *   - `"e2e-self"` — E2E encrypted to the local user only. Used by
 *     Personal Workspace and Secrets channels (recipient set is a
 *     singleton).
 *   - `"e2e-multi"` — E2E encrypted to every participant. Used by
 *     DM channels. The hook expects `recipients` to be populated
 *     when this is set.
 */
export type ChannelEncryptionKind =
  | "atrest"
  | "e2e-self"
  | "e2e-secret"
  | "e2e-multi";

interface UseMessagesValue {
  status: DataStatus;
  messages: MessageView[];
  send: (body: string, mentions?: string[], parentMessageXid?: string) => Promise<void>;
  sendFile: (result: SendFileInput) => Promise<void>;
  toggleReact: (messageXid: string, emoji: string) => Promise<void>;
  togglePin: (messageXid: string) => Promise<void>;
  edit: (messageXid: string, body: string) => Promise<void>;
  delete: (messageXid: string) => Promise<void>;
}

/** Minimal shape `useMessages.sendFile` accepts. Drives both the
 * optimistic file-row and the wire body it serializes. */
export interface SendFileInput {
  attachmentXid: string;
  filename: string;
  mime: string;
  byteSize: number;
}

export function useMessages(
  channelXid: string | null,
  fallback: MessageView[] = [],
  encryptionKind: ChannelEncryptionKind = "atrest",
  recipients: ReadonlyArray<E2eRecipient> = [],
  selfAccountXid: string,
  selfName: string = "you",
  selfUsername: string = "",
): UseMessagesValue {
  const [messages, setMessages] = useState<MessageView[]>(fallback);
  const [status, setStatus] = useState<DataStatus>(
    authed() ? "loading" : "idle",
  );
  const [messageVersion, setMessageVersion] = useState(0);
  // Track whether the current channel is server-backed so mutation
  // helpers know which path to take.
  const isLive = useRef(false);
  const loadedChannel = useRef<string | null>(null);
  const reactionMutations = useRef(new Set<string>());

  // Initial load.
  useEffect(() => {
    if (channelXid === null) {
      setMessages(fallback);
      setStatus("idle");
      isLive.current = false;
      return;
    }
    if (!authed()) {
      setMessages(fallback);
      setStatus("idle");
      isLive.current = false;
      return;
    }
    if (
      encryptionKind !== "atrest" &&
      selfAccountXid.trim().length === 0
    ) {
      setMessages(fallback);
      setStatus("loading");
      isLive.current = false;
      return;
    }

    let cancelled = false;
    if (loadedChannel.current !== channelXid) {
      isLive.current = false;
      setStatus("loading");
    }
    const loadMessages =
      encryptionKind === "e2e-multi"
        ? api.dms.messages(channelXid, { limit: 50 })
        : api.messages.list(channelXid, { limit: 50 });
    loadMessages
      .then(async (res) => {
        if (cancelled) return;
        const ordered = [...res.messages].sort(compareWireMessages);
        const initial = ordered.map((m) => toMessageView(m, selfUsername, selfAccountXid));
        setMessages(initial);
        setStatus("ready");
        isLive.current = true;
        loadedChannel.current = channelXid;
        // Hydrate any E2E rows by decrypting in the background.
        // Failures land as `(could not decrypt)` so the row still
        // renders something the user can act on.
        if (encryptionKind !== "atrest") {
          const decrypted = await Promise.all(
            ordered.map((m) =>
              decryptIfNeeded(
                m,
                channelXid,
                encryptionKind,
                selfAccountXid,
              ),
            ),
          );
          if (cancelled) return;
          setMessages(decrypted);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (!isLive.current) {
          setMessages(fallback);
          setStatus("error");
        }
        // Soft-log only.
        void messageOf(err);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelXid, encryptionKind, selfAccountXid, messageVersion]);

  // Realtime: subscribe to the channel topic. New `message.created`
  // events append to local state; `message.deleted` removes; pin /
  // reaction events update in place.
  useRealtimeSubscription(
    channelXid !== null
      ? [
          encryptionKind === "e2e-multi"
            ? `dm:${channelXid}`
            : `channel:${channelXid}`,
        ]
      : [],
    (frame) => {
      if (!isLive.current) return;
      if (
        frame.type === "message.created" ||
        frame.type === "message.edited" ||
        frame.type === "message.deleted" ||
        frame.type === "reaction.added" ||
        frame.type === "reaction.removed" ||
        frame.type === "pin.added" ||
        frame.type === "pin.removed"
      ) {
        setMessageVersion((value) => value + 1);
      }
    },
  );
  const resyncMessages = useCallback(
    () => setMessageVersion((value) => value + 1),
    [],
  );
  useRealtimeResync(resyncMessages);

  const send = useCallback(
    async (body: string, mentions: string[] = [], parentMessageXid?: string) => {
      const trimmed = body.trim();
      if (trimmed.length === 0 || channelXid === null) return;
      // Optimistic local row. Real send paths will reuse the
      // `xid` once the server returns it.
      const optimistic: MessageView = {
        xid: `tmp-${Math.random().toString(36).slice(2, 10)}`,
        authorXid: selfAccountXid || "me",
        createdAt: new Date().toISOString(),
        authorName: selfName,
        authorRole: "",
        authorRoleColor: "text-strawberry-600",
        timestamp: "just now",
        date: new Date().toDateString(),
        body: trimmed,
        kind: encryptionKind === "e2e-secret" ? "secret" : "text",
        ...(parentMessageXid !== undefined ? {
          replyTo: (() => {
            const parent = messages.find((message) => message.xid === parentMessageXid);
            return { xid: parentMessageXid, authorName: parent?.authorName ?? "message", preview: parent?.body.slice(0, 100) ?? "Reply" };
          })(),
        } : {}),
      };
      setMessages((prev) => [...prev, optimistic]);
      if (!isLive.current) return;
      const finishSend = beginUpdateActivity();
      try {
        let created: WireMessage;
        if (encryptionKind === "atrest") {
          created = await api.messages.send(
            channelXid,
            {
              storage_kind: "atrest",
              body: trimmed,
              ...(mentions.length > 0 ? { mentions } : {}),
              ...(parentMessageXid !== undefined ? { parent_message_xid: parentMessageXid } : {}),
            },
            { idempotencyKey: optimistic.xid },
          );
        } else {
          // E2E send. Encrypt to either the local user (e2e-self)
          // or to every participant (e2e-multi), then forward the
          // serialized envelope as base64.
          const identity = await loadIdentity(selfAccountXid);
          if (identity === null) {
            throw new Error(
              "Local identity key isn't loaded. Sign in first.",
            );
          }
          const recipientList: E2eRecipient[] =
            encryptionKind === "e2e-self" || encryptionKind === "e2e-secret"
              ? [
                  {
                    accountIdHash: identity.publicKeyHash,
                    identityPublicKey: identity.publicKeyRaw,
                  },
                ]
              : [...recipients];
          if (recipientList.length === 0) {
            throw new Error(
              "No recipients available for E2E encryption.",
            );
          }
          const envelope = await encryptE2e({
            plaintext: TEXT_ENCODER.encode(trimmed),
            recipients: recipientList,
            channelId: channelXid,
            // The server assigns the real message xid; for the
            // outgoing AAD we use the optimistic id so the round-
            // trip checks bind. The server replays this AAD on
            // realtime fan-out untouched.
            messageId: optimistic.xid,
          });
          const envelopeB64 = bytesToB64(serializeE2eEnvelope(envelope));
          const request = {
            storage_kind: "e2e" as const,
            envelope: envelopeB64,
            envelope_kind:
              encryptionKind === "e2e-secret"
                ? ("secrets" as const)
                : ("e2e" as const),
            e2e_context_id: optimistic.xid,
            ...(parentMessageXid !== undefined ? { parent_message_xid: parentMessageXid } : {}),
          };
          created =
            encryptionKind === "e2e-multi"
              ? await api.dms.sendMessage(channelXid, request, {
                  idempotencyKey: optimistic.xid,
                })
              : await api.messages.send(channelXid, request, {
                  idempotencyKey: optimistic.xid,
                });
        }
        setMessages((prev) =>
          prev.map((m) =>
            m.xid === optimistic.xid
              ? toMessageViewWithIdentity(created, encryptionKind, trimmed, selfAccountXid)
              : m,
          ),
        );
      } catch (err) {
        // Mark the optimistic row as failed by appending a footer
        // — keeps the user's text on screen so they can retry.
        setMessages((prev) =>
          prev.map((m) =>
            m.xid === optimistic.xid
              ? { ...m, body: `${m.body}\n\n_(failed: ${messageOf(err)})_` }
              : m,
          ),
        );
      } finally { finishSend(); }
    },
    [channelXid, encryptionKind, messages, recipients, selfAccountXid, selfName],
  );

  const sendFile = useCallback(
    async (input: SendFileInput) => {
      if (channelXid === null) return;

      // Optimistic file row. The body carries our tagged file
      // marker so when the server echoes it back we can decode the
      // metadata and render the same file card on every client.
      const sizeLabel = formatBytes(input.byteSize);
      const optimisticXid = `tmp-${Math.random().toString(36).slice(2, 10)}`;
      const optimistic: MessageView = {
        xid: optimisticXid,
        authorXid: selfAccountXid || "me",
        createdAt: new Date().toISOString(),
        authorName: selfName,
        authorRole: "",
        authorRoleColor: "text-strawberry-600",
        timestamp: "just now",
        date: new Date().toDateString(),
        body: `📎 ${input.filename}`,
        kind: "file",
        file: {
          name: input.filename,
          sizeLabel,
          mime: input.mime,
          attachmentXid: input.attachmentXid,
        },
      };
      setMessages((prev) => [...prev, optimistic]);
      if (!isLive.current) return;

      const wireBody = encodeFileMessageBody(input);
      const finishSend = beginUpdateActivity();
      try {
        let created: WireMessage;
        if (encryptionKind === "atrest") {
          created = await api.messages.send(
            channelXid,
            {
              storage_kind: "atrest",
              body: wireBody,
              attachment_xids: [input.attachmentXid],
            },
            { idempotencyKey: optimisticXid },
          );
        } else {
          // E2E send wraps the same tagged body so participants can
          // decode the file metadata client-side after decrypting.
          const identity = await loadIdentity(selfAccountXid);
          if (identity === null) {
            throw new Error(
              "Local identity key isn't loaded. Sign in first.",
            );
          }
          const recipientList: E2eRecipient[] =
            encryptionKind === "e2e-self" || encryptionKind === "e2e-secret"
              ? [
                  {
                    accountIdHash: identity.publicKeyHash,
                    identityPublicKey: identity.publicKeyRaw,
                  },
                ]
              : [...recipients];
          if (recipientList.length === 0) {
            throw new Error("No recipients available for E2E encryption.");
          }
          const envelope = await encryptE2e({
            plaintext: TEXT_ENCODER.encode(wireBody),
            recipients: recipientList,
            channelId: channelXid,
            messageId: optimisticXid,
          });
          const envelopeB64 = bytesToB64(serializeE2eEnvelope(envelope));
          const request = {
            storage_kind: "e2e" as const,
            envelope: envelopeB64,
            envelope_kind:
              encryptionKind === "e2e-secret"
                ? ("secrets" as const)
                : ("e2e" as const),
            e2e_context_id: optimisticXid,
            attachment_xids: [input.attachmentXid],
          };
          created =
            encryptionKind === "e2e-multi"
              ? await api.dms.sendMessage(channelXid, request, {
                  idempotencyKey: optimisticXid,
                })
              : await api.messages.send(channelXid, request, {
                  idempotencyKey: optimisticXid,
                });
        }
        setMessages((prev) =>
          prev.map((m) =>
            m.xid === optimisticXid
              ? toMessageViewWithIdentity(created, encryptionKind, wireBody, selfAccountXid)
              : m,
          ),
        );
      } catch (err) {
        setMessages((prev) =>
          prev.map((m) =>
            m.xid === optimisticXid
              ? { ...m, body: `${m.body}\n\n_(failed: ${messageOf(err)})_` }
              : m,
          ),
        );
      } finally { finishSend(); }
    },
    [channelXid, encryptionKind, recipients, selfAccountXid, selfName],
  );

  const toggleReact = useCallback(
    async (messageXid: string, emoji: string) => {
      if (!isLive.current) return;
      const mutationKey = `${messageXid}\u0000${emoji}`;
      if (reactionMutations.current.has(mutationKey)) return;
      const message = messages.find((candidate) => candidate.xid === messageXid);
      if (message === undefined) return;
      const previous = message.reactions?.find((reaction) => reaction.emoji === emoji);
      const willAdd = previous?.mine !== true;
      reactionMutations.current.add(mutationKey);

      // Optimistically apply the already-decided operation. The old version
      // decided inside a React updater, so the request could run before that
      // updater and send the opposite operation during quick clicks.
      setMessages((prev) =>
        prev.map((m) => {
          if (m.xid !== messageXid) return m;
          const reactions = setOwnReaction(m.reactions ?? [], emoji, willAdd);
          return { ...m, reactions };
        }),
      );
      try {
        if (willAdd) await api.messages.react(messageXid, { emoji });
        else await api.messages.unreact(messageXid, emoji);
      } catch {
        // Restore the exact prior reaction entry on failure.
        setMessages((prev) =>
          prev.map((m) => {
            if (m.xid !== messageXid) return m;
            const reactions = (m.reactions ?? []).filter((reaction) => reaction.emoji !== emoji);
            if (previous !== undefined) reactions.push(previous);
            return { ...m, reactions };
          }),
        );
      } finally {
        reactionMutations.current.delete(mutationKey);
        // Reconcile with canonical membership, including activity on other devices.
        setMessageVersion(value => value + 1);
      }
    },
    [messages],
  );

  const togglePin = useCallback(
    async (messageXid: string) => {
      let nextPinned = false;
      setMessages((prev) =>
        prev.map((m) => {
          if (m.xid !== messageXid) return m;
          nextPinned = !(m.pinned ?? false);
          return { ...m, pinned: nextPinned };
        }),
      );
      if (!isLive.current || channelXid === null) return;
      try {
        if (encryptionKind === "e2e-multi") {
          if (nextPinned) await api.dms.pin(channelXid, messageXid);
          else await api.dms.unpin(channelXid, messageXid);
        } else if (nextPinned) await api.messages.pin(channelXid, messageXid);
        else await api.messages.unpin(channelXid, messageXid);
      } catch {
        // Revert on failure.
        setMessages((prev) =>
          prev.map((m) =>
            m.xid === messageXid ? { ...m, pinned: !nextPinned } : m,
          ),
        );
      }
    },
    [channelXid, encryptionKind],
  );

  const edit = useCallback(
    async (messageXid: string, body: string) => {
      const trimmed = body.trim();
      if (trimmed.length === 0 || channelXid === null || !isLive.current) return;
      const current = messages.find((message) => message.xid === messageXid);
      if (current === undefined) return;
      const finishEdit = beginUpdateActivity();
      try {
      let updated: WireMessage;
      if (encryptionKind === "atrest") {
        updated = await api.messages.edit(messageXid, {
          storage_kind: "atrest",
          body: trimmed,
        });
      } else {
        const identity = await loadIdentity(selfAccountXid);
        if (identity === null) throw new Error("Local identity key isn't loaded. Sign in first.");
        const recipientList: E2eRecipient[] =
          encryptionKind === "e2e-self" || encryptionKind === "e2e-secret"
            ? [{ accountIdHash: identity.publicKeyHash, identityPublicKey: identity.publicKeyRaw }]
            : [...recipients];
        if (recipientList.length === 0) throw new Error("No recipients available for E2E encryption.");
        const contextId = current.encryptionContextId ?? messageXid;
        const envelope = await encryptE2e({
          plaintext: TEXT_ENCODER.encode(trimmed),
          recipients: recipientList,
          channelId: channelXid,
          messageId: contextId,
        });
        const request = {
          storage_kind: "e2e" as const,
          envelope: bytesToB64(serializeE2eEnvelope(envelope)),
          envelope_kind: encryptionKind === "e2e-secret" ? ("secrets" as const) : ("e2e" as const),
          e2e_context_id: contextId,
        };
        updated = encryptionKind === "e2e-multi"
          ? await api.dms.editMessage(channelXid, messageXid, request)
          : await api.messages.edit(messageXid, request);
      }
      const view = toMessageViewWithIdentity(updated, encryptionKind, trimmed, selfAccountXid);
      setMessages((currentMessages) => currentMessages.map((message) =>
        message.xid === messageXid ? { ...view, editedAt: updated.edited_at } : message,
      ));
      } finally { finishEdit(); }
    },
    [channelXid, encryptionKind, messages, recipients, selfAccountXid],
  );

  const deleteMessage = useCallback(
    async (messageXid: string) => {
      if (channelXid === null || !isLive.current) return;
      // Keep the row mounted until the permission check succeeds, so a failed
      // moderation request can show its error without restoring stale history.
      if (encryptionKind === "e2e-multi") await api.dms.deleteMessage(channelXid, messageXid);
      else await api.messages.delete(messageXid);
      setMessages((current) => current.filter((message) => message.xid !== messageXid));
    },
    [channelXid, encryptionKind],
  );

  return useMemo(
    () => ({ status, messages: resolveReplyPreviews(messages, encryptionKind === "e2e-secret"), send, sendFile, toggleReact, togglePin, edit, delete: deleteMessage }),
    [status, messages, encryptionKind, send, sendFile, toggleReact, togglePin, edit, deleteMessage],
  );
}

// ---------------------------------------------------------------------------
// Wire → view-model adapters
// ---------------------------------------------------------------------------

function compareWireMessages(left: WireMessage, right: WireMessage): number {
  const timeDelta = Date.parse(left.created_at) - Date.parse(right.created_at);
  return timeDelta !== 0 ? timeDelta : left.xid.localeCompare(right.xid);
}

function toSpaceView(s: {
  xid: string;
  space_type: SpaceView["spaceType"];
  name: string;
  icon_url: string | null;
  owner_account_id: string;
  notification_muted: boolean;
}): SpaceView {
  const initials = s.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
  return {
    xid: s.xid,
    spaceType: s.space_type,
    name: s.name,
    ownerAccountXid: s.owner_account_id,
    notificationMuted: s.notification_muted,
    iconLabel: initials || "L",
    iconUrl: s.icon_url,
  };
}

function toChannelView(c: {
  xid: string;
  name: string;
  channel_type: "text" | "snippets" | "secrets" | "files";
}): ChannelView {
  return {
    xid: c.xid,
    name: c.name,
    channelType: c.channel_type,
  };
}

function toMessageView(m: WireMessage, selfUsername: string = "", selfAccountXid: string = ""): MessageView {
  const created = new Date(m.created_at);
  const rawBody = m.body ?? (m.storage_kind === "e2e" ? "(decrypting…)" : "");
  const decoded = decodeFileMessageBody(rawBody);
  const baseKind: MessageView["kind"] =
    decoded !== null
      ? "file"
      : m.storage_kind === "e2e"
        ? "secret"
        : "text";
  return {
    xid: m.xid,
    createdAt: m.created_at,
    editedAt: m.edited_at,
    encryptionContextId: m.encryption_context_id,
    authorXid: m.author_account_xid,
    authorName: m.author_display_name,
    authorAvatarUrl: m.author_avatar_url,
    authorRole: "",
    authorRoleColor: "text-cloud-500",
    timestamp: created.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    }),
    date: created.toDateString(),
    body: decoded !== null ? (decoded.caption ?? `📎 ${decoded.filename}`) : rawBody,
    mentionsMe: selfUsername.length > 0 && mentionsUsername(rawBody, selfUsername),
    kind: baseKind,
    file:
      decoded !== null
        ? {
            name: decoded.filename,
            sizeLabel: formatBytes(decoded.byteSize),
            mime: decoded.mime,
            attachmentXid: decoded.attachmentXid,
          }
        : undefined,
    pinned: m.pinned,
    reactions: reactionsForViewer(m.reactions ?? [], selfAccountXid),
    expiresAt: m.expires_at,
    replyTo:
      m.parent_message_xid !== undefined
        ? {
            xid: m.parent_message_xid,
            authorName: "",
            preview: "Original message isn’t loaded",
            unavailable: true,
          }
        : undefined,
  };
}

/**
 * Variant used by the optimistic `send` path when the server has
 * already echoed back the wire message and we have local context
 * (i.e. we know the encryption kind). The base body comes back as
 * null on E2E sends, so we patch in the user's plaintext that we
 * just encrypted.
 */
function toMessageViewWithIdentity(
  m: WireMessage,
  encryptionKind: ChannelEncryptionKind,
  plaintext: string,
  selfAccountXid: string,
): MessageView {
  if (encryptionKind === "atrest") return toMessageView(m, "", selfAccountXid);
  return toDecryptedMessageView(m, plaintext, encryptionKind, selfAccountXid);
}

function mentionsUsername(body: string, username: string): boolean {
  if (/(^|\s)@(everyone|here)\b/i.test(body)) return true;
  const escaped = username.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|\\s)@${escaped}(?=\\s|$|[.,!?;:])`, "i").test(body);
}

function toDecryptedMessageView(
  m: WireMessage,
  plaintext: string,
  encryptionKind: ChannelEncryptionKind,
  selfAccountXid: string,
): MessageView {
  const view = toMessageView({ ...m, body: plaintext }, "", selfAccountXid);
  return {
    ...view,
    kind:
      view.file !== undefined
        ? "file"
        : encryptionKind === "e2e-secret"
          ? "secret"
          : "text",
  };
}

/**
 * Decrypt an E2E message in place. Returns the message with `body`
 * replaced by the plaintext, or with `(could not decrypt)` on a
 * failure. Failure-mode is intentional: a single bad row should
 * not break the whole channel render.
 */
async function decryptIfNeeded(
  m: WireMessage,
  channelXid: string,
  encryptionKind: ChannelEncryptionKind,
  selfAccountXid: string,
): Promise<MessageView> {
  const view = toMessageView(m, "", selfAccountXid);
  if (m.storage_kind !== "e2e" || m.envelope === null) return view;
  if (encryptionKind === "atrest") return view;
  try {
    const identity = await loadIdentity(selfAccountXid);
    if (identity === null) {
      return { ...view, body: "(sign in to decrypt)" };
    }
    const envelopeBytes = b64ToBytes(m.envelope);
    const envelope = parseE2eEnvelope(envelopeBytes);
    // AAD round-trip check happens inside decryptE2e.
    void buildE2eAad;
    const plaintext = await decryptE2e({
      envelope,
      recipientPrivateKey: identity.ecdhPrivateKey,
      recipientAccountIdHash: identity.publicKeyHash,
      channelId: channelXid,
      messageId: m.encryption_context_id ?? m.xid,
    });
    return toDecryptedMessageView(
      m,
      TEXT_DECODER.decode(plaintext),
      encryptionKind,
      selfAccountXid,
    );
  } catch (err) {
    void err;
    return { ...view, body: "(could not decrypt)" };
  }
}

function bytesToB64(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

function messageOf(err: unknown): string {
  if (err instanceof ApiCallError) {
    return err.envelope?.message ?? `HTTP ${err.status}`;
  }
  if (err instanceof Error) return err.message;
  return "Unknown error";
}

// ---------------------------------------------------------------------------
// File-message body codec
//
// File attachments piggy-back on the regular at-rest / E2E message
// surface for v1: the wire body is a tagged JSON envelope so every
// participant decodes the same metadata back into a `MessageView.file`.
// The format is intentionally simple — a control-character sentinel
// keeps it from colliding with anything a user could realistically type
// into a chat box.
// ---------------------------------------------------------------------------

const FILE_BODY_PREFIX = "\u0001LINK_FILE_V1\u0001";

interface DecodedFileBody {
  attachmentXid: string;
  filename: string;
  mime: string;
  byteSize: number;
  caption: string | null;
}

function encodeFileMessageBody(input: SendFileInput, caption?: string): string {
  const meta = {
    attachment_xid: input.attachmentXid,
    filename: input.filename,
    mime: input.mime,
    byte_size: input.byteSize,
  };
  const tail = caption !== undefined && caption.length > 0 ? `\n${caption}` : "";
  return `${FILE_BODY_PREFIX}${JSON.stringify(meta)}\u0001${tail}`;
}

function decodeFileMessageBody(body: string): DecodedFileBody | null {
  if (!body.startsWith(FILE_BODY_PREFIX)) return null;
  const remainder = body.slice(FILE_BODY_PREFIX.length);
  const terminator = remainder.indexOf("\u0001");
  if (terminator === -1) return null;
  const json = remainder.slice(0, terminator);
  const captionRaw = remainder.slice(terminator + 1);
  try {
    const parsed = JSON.parse(json) as {
      attachment_xid?: unknown;
      filename?: unknown;
      mime?: unknown;
      byte_size?: unknown;
    };
    if (
      typeof parsed.attachment_xid !== "string" ||
      typeof parsed.filename !== "string" ||
      typeof parsed.mime !== "string" ||
      typeof parsed.byte_size !== "number"
    ) {
      return null;
    }
    const caption = captionRaw.startsWith("\n")
      ? captionRaw.slice(1).length > 0
        ? captionRaw.slice(1)
        : null
      : captionRaw.length > 0
        ? captionRaw
        : null;
    return {
      attachmentXid: parsed.attachment_xid,
      filename: parsed.filename,
      mime: parsed.mime,
      byteSize: parsed.byte_size,
      caption,
    };
  } catch {
    return null;
  }
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  const rounded = value >= 100 || i === 0 ? value.toFixed(0) : value.toFixed(1);
  return `${rounded} ${units[i]}`;
}
